import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetOne, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { Box } from '@mui/material';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getRefreshToken, getToken } from '../authentication/authProvider';
import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';
import { formatAddress } from '../pricing/pickupLocation';
import { useTransportCalculator } from '../pricing/useTransportCalculator';
import { usePickupLocation } from '../pricing/usePickupLocation';
import { useZoneStatus } from '../pricing/useZoneStatus';
import { BILL_ROUND_TRIP, haversineKm, LatLng, RATES, ZoneRegion } from '../pricing/zone';
import { Contract } from '../types';

const money = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });

/**
 * Transport on the contract, with the actions that matter.
 *
 * Shows what the zone says now and what was actually charged — they can
 * legitimately differ, and that difference is worth seeing. Calculating and
 * writing the line are explicit: a number a family will be held to should never
 * move because someone opened a page.
 */
export const ContractTransportSummary = () => {
  const contract = useRecordContext<Contract>();
  const notify = useNotify();
  const refresh = useRefresh();
  const funeralId = (contract as any)?.funeral_id;

  const { isLoaded: mapsLoaded } = useLoadGoogleMaps(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  );
  const { data: funeral } = useGetOne(
    'funerals',
    { id: funeralId },
    { enabled: funeralId != null },
  );

  const [updateFuneral] = useUpdate();
  const [updateContract] = useUpdate();
  const [addressOpen, setAddressOpen] = useState(false);
  const [picked, setPicked] = useState<any>(null);

  /**
   * Store coordinates the moment they are resolved.
   *
   * Without this the same address is geocoded again on every visit — money for
   * an answer we already had, and geocoders do not always return the same point
   * twice, which would quietly move a charge.
   */
  const persistCoords = useCallback(
    (coords: LatLng, overridden: boolean) => {
      if (!funeral) return;
      const field = overridden ? 'pickup_address' : 'place_of_death_address';
      void updateFuneral('funerals', {
        id: funeral.id,
        // Stamped with the address it came from, so a later edit invalidates it.
        data: {
          [field]: {
            ...(funeral as any)[field],
            ...coords,
            geocoded_from: formatAddress((funeral as any)[field]),
          },
        },
        previousData: funeral,
      });
    },
    [funeral, updateFuneral],
  );

  const status = useZoneStatus((funeral as any) ?? {});
  const pickup = usePickupLocation((funeral as any) ?? null);
  const region: ZoneRegion =
    ((funeral as any)?.region ?? '').toLowerCase() === 'ontario' ? 'ontario' : 'quebec';
  const rate = RATES[region];
  const { result, error, busy, calculate } = useTransportCalculator(
    region,
    pickup,
    persistCoords,
  );

  const line = ((contract as any)?.lines ?? []).find(
    (l: any) => String(l?.item) === rate.quickbooksItemId,
  );
  const priced = (contract as any)?.transport_pricing;

  /**
   * Was this line priced from an address the case no longer has?
   *
   * Detected by comparing the coordinates stored when the charge was
   * calculated against the case's coordinates now — rather than by hooking the
   * funeral's save. A save hook only catches one route: the address also moves
   * by import, by another user, or by an API write, and each of those would
   * leave a stale price with nobody told. Comparing what we recorded against
   * what is true catches all of them.
   *
   * 150 m, because a re-geocode of the same address rarely lands on the exact
   * same point and a few metres is not a repricing.
   */
  const stale = useMemo(() => {
    const before: LatLng | undefined = priced?.pickup;
    const now = status.state === 'in_zone' || status.state === 'out_of_zone' ? status.coords : null;
    if (!before?.lat || !now) return false;
    return haversineKm(before, now) > 0.15;
  }, [priced, status]);

  /**
   * Record an in-zone determination, without touching a line.
   *
   * "This case is covered" is a pricing decision too, and it was going
   * unrecorded: `applyLine` returns early for an in-zone result, so a covered
   * case left no trace. Move the address afterwards and there was nothing to
   * compare against, so nothing said the answer had changed — the case simply
   * looked covered, silently, from a coordinate that no longer applied.
   *
   * Persisted automatically rather than behind a button because it writes no
   * line and changes no total. The rule this feature protects is that money
   * never moves on its own; an audit record is not money.
   */
  useEffect(() => {
    if (!result?.inZone || !contract) return;
    const already = priced?.in_zone === true && priced?.pickup?.lat === result.coords.lat;
    if (already) return;

    void updateContract('contracts', {
      id: (contract as any).id,
      data: {
        transport_pricing: {
          zone_version: 'v1',
          pickup: result.coords,
          in_zone: true,
          municipality: result.municipality,
          calculated_at: new Date().toISOString(),
        },
      },
      previousData: contract,
    });
  }, [result, contract, priced, updateContract]);

  /**
   * Recompute the contract's totals and push the estimate to QuickBooks.
   *
   * Writing `lines` is only half the job: subtotal, taxes and total_amount are
   * recomputed by this endpoint, not by the row update. Without it a line
   * appears while every total below it still reflects the contract without it,
   * and QuickBooks never hears about the change at all.
   *
   * Needed just as much when a charge is REMOVED — a stale total that still
   * includes a deleted line overcharges a family, which is the worse direction.
   *
   * Same endpoint the contract save button calls.
   */
  const syncEstimate = useCallback(
    async (successMessage: string) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_PAPERMAN_API_URL}quickbooks/estimates/${(contract as any).id}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${await getToken()}`,
              'Refresh-Token': await getRefreshToken(),
            },
          },
        );
        if (res.ok) {
          notify(successMessage, { type: 'success' });
        } else {
          const body = await res.json().catch(() => ({}));
          const detail = body?.error?.Error?.[0]?.Detail ?? body?.error;
          // The line change IS saved; only the sync failed. Say which, so
          // nobody repeats an edit that already landed.
          notify(`Saved, but the totals did not update in QuickBooks. ${detail ?? ''}`, {
            type: 'warning',
            autoHideDuration: 8000,
            multiLine: true,
          });
        }
      } catch (e: any) {
        notify(`Saved, but the totals did not update: ${e?.message ?? 'sync failed'}`, {
          type: 'warning',
          autoHideDuration: 8000,
          multiLine: true,
        });
      } finally {
        refresh();
      }
    },
    [contract, notify, refresh],
  );

  /**
   * Take the transport charge off, for a case that is now inside the zone.
   *
   * A button rather than automatic: this removes money from a contract, and
   * the rule the whole panel keeps is that money never moves on its own.
   */
  const removeLine = () => {
    const next = ((contract as any)?.lines ?? []).filter(
      (l: any) => String(l?.item) !== rate.quickbooksItemId,
    );
    void updateContract(
      'contracts',
      {
        id: (contract as any).id,
        data: {
          lines: next,
          transport_pricing: {
            ...(priced ?? {}),
            in_zone: true,
            removed_at: new Date().toISOString(),
          },
        },
        previousData: contract,
      },
      {
        onSuccess: () => void syncEstimate('Transport charge removed'),
        onError: (e: any) =>
          notify(e?.message ?? 'Could not remove the line', { type: 'warning' }),
      },
    );
  };

  const applyLine = () => {
    if (!result || result.inZone) return;
    const km = Math.round(result.billableKm);
    // `quantity` on a contract line is an integer column, so a fractional
    // kilometre is rejected outright. At $2-3/km the rounding is worth cents,
    // and a whole number is easier for a family to check than 42.1.
    const next = [...((contract as any)?.lines ?? [])];
    const entry = {
      item: rate.quickbooksItemId,
      name: `${rate.quickbooksItemId === '34' ? 'ON' : 'QC'} | Outside Zone Transportation`,
      description: `${km} km${BILL_ROUND_TRIP ? ' round trip' : ''} from the zone edge at ${result.crossingMunicipality}${result.measured ? '' : ' (estimated)'}`,
      price: rate.perKm,
      quantity: km,
      total: Number((km * rate.perKm).toFixed(2)),
      // Mirrors what a hand-entered transport line carries. `tax` is the
      // QuickBooks tax code and is mandatory — without it the estimate sync
      // refuses the whole contract, not just this line.
      tax: rate.quickbooksTaxCode,
      type: '_',
    };
    const at = next.findIndex((l: any) => String(l?.item) === rate.quickbooksItemId);
    if (at >= 0) next[at] = { ...next[at], ...entry };
    else next.push(entry);

    void updateContract(
      'contracts',
      {
        id: (contract as any).id,
        data: {
          lines: next,
          transport_pricing: {
            zone_version: 'v1',
            pickup: result.coords,
            in_zone: false,
            crossing: result.crossing,
            crossing_municipality: result.crossingMunicipality,
            billable_km: km,
            measured_km: Number(result.billableKm.toFixed(2)),
            rate: rate.perKm,
            quickbooks_item_id: rate.quickbooksItemId,
            distance_method: result.measured ? 'routed' : 'straight_line_estimate',
            calculated_at: new Date().toISOString(),
          },
        },
        previousData: contract,
      },
      {
        onSuccess: () => void syncEstimate('Transport line updated'),
        onError: (e: any) => notify(e?.message ?? 'Could not save the line', { type: 'warning' }),
      },
    );
  };

  const savePickup = () => {
    const placeId = picked?.value?.place_id;
    if (!placeId || !window.google?.maps?.places || !funeral) return;

    new window.google.maps.places.PlacesService(document.createElement('div')).getDetails(
      { placeId, fields: ['address_components', 'geometry'] },
      (place: any, s: any) => {
        if (s !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
        let num = '', route = '', city = '', prov = '', postal = '', country = '';
        place.address_components?.forEach((c: any) => {
          if (c.types.includes('street_number')) num = c.long_name;
          else if (c.types.includes('route')) route = c.long_name;
          else if (c.types.includes('locality') || c.types.includes('sublocality_level_1')) city = c.long_name;
          else if (c.types.includes('administrative_area_level_1')) prov = c.short_name;
          else if (c.types.includes('postal_code')) postal = c.long_name;
          else if (c.types.includes('country')) country = c.short_name;
        });
        const loc = place.geometry?.location;
        void updateFuneral(
          'funerals',
          {
            id: funeral.id,
            data: {
              pickup_address: {
                adresse: [num, route].filter(Boolean).join(' '),
                municipalite: city,
                province: prov,
                codePostal: postal,
                pays: country || 'Canada',
                ...(loc ? { lat: loc.lat(), lng: loc.lng() } : {}),
              },
            },
            previousData: funeral,
          },
          {
            onSuccess: () => {
              notify('Pickup address set — recalculate to reprice', { type: 'info' });
              setAddressOpen(false);
              setPicked(null);
              refresh();
            },
          },
        );
      },
    );
  };

  const clearPickup = () => {
    if (!funeral) return;
    // Null, not blanks: an empty pickup_address means "collect at the place of
    // death", and a row of empty strings reads as a deliberate override.
    void updateFuneral(
      'funerals',
      { id: funeral.id, data: { pickup_address: null }, previousData: funeral },
      {
        onSuccess: () => {
          notify('Pickup reset to the place of death', { type: 'info' });
          setAddressOpen(false);
          refresh();
        },
      },
    );
  };

  if (!funeralId) return null;

  const overridden = status.state !== 'no_address' && status.pickup?.overridden;

  // One vertical rhythm for the whole panel: the container owns the spacing via
  // `gap`, so no block sets its own top margin and they cannot end up unevenly
  // spaced as blocks appear and disappear.
  return (
    // Spacing via MUI's `sx`, not a Tailwind class. This sits directly under
    // LinesShow — an ArrayField/Datagrid rendering a MUI table — and a utility
    // class on a plain div left the card flush against the last line item.
    // `sx` generates a real class in MUI's own layer, so it lands regardless of
    // what the table around it sets. Padding rather than margin so nothing can
    // collapse against the table above.
    <Box className="cleo-ui" sx={{ pt: 4, pb: 2 }}>
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">Transport</span>
        {status.state === 'in_zone' && <Badge variant="success">Inside Zone 1</Badge>}
        {status.state === 'out_of_zone' && <Badge variant="warning">Outside Zone 1</Badge>}
        {overridden && (
          <Badge variant="outline">pickup overridden</Badge>
        )}
      </div>

      {status.state === 'no_address' ? (
        <p className="text-sm text-muted-foreground">
          No address on the case — the zone can't be checked.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {status.pickup.label}
          {status.state === 'in_zone' && ` — ${status.municipality}, transport included`}
          {status.state === 'not_located' && ' — not checked, no coordinates stored'}
        </p>
      )}

      {line && (
        <p className="text-sm">
          Charged: {line.quantity} km × {money(Number(line.price ?? 0))} ={' '}
          <strong>{money(Number(line.total ?? 0))}</strong>
        </p>
      )}

      {stale && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          The pickup address has changed since this was last checked
          {priced?.in_zone ? ' and found inside the zone' : ''} — recalculate.
        </p>
      )}

      {line && status.state === 'in_zone' && (
        // Inside the zone with a charge still on the contract: the address
        // moved in, and nobody took the line off. This one overcharges a
        // family, so it gets an action rather than only a warning.
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-amber-600 dark:text-amber-500">
            Now inside Zone 1, but a transport charge is still on this contract.
          </p>
          <Button size="sm" variant="outline" onClick={removeLine}>
            Remove the transport line
          </Button>
        </div>
      )}

      {!line && status.state === 'out_of_zone' && (
        // Outside the zone with nothing billed is how a transfer gets given away.
        <p className="text-sm text-amber-600 dark:text-amber-500">
          Outside the zone with no transport line on this contract.
        </p>
      )}

      {result && !result.inZone && (
        <div className="flex flex-col items-start gap-2 rounded-md border border-border bg-muted/40 p-3">
          <div>
            <p className="text-sm">
              {result.billableKm.toFixed(1)} km × {money(rate.perKm)} ={' '}
              <strong>{money(result.amount)}</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              From the zone edge at {result.crossingMunicipality}
              {result.measured ? '' : ' — estimated, no driving route available'}
            </p>
          </div>
          {line && Number(line.total ?? 0) !== Number(result.amount.toFixed(2)) && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              Replaces the current line: {money(Number(line.total ?? 0))} →{' '}
              {money(result.amount)}
            </p>
          )}
          <Button size="sm" onClick={applyLine}>
            {line ? 'Update the line' : 'Add to contract'}
          </Button>
        </div>
      )}

      {result?.inZone && (
        <p className="text-sm">
          {result.municipality} — inside the zone, nothing to add. Recorded, so a later
          address change will flag this.
        </p>
      )}

      {error && (
        <p className="text-sm text-amber-600 dark:text-amber-500">
          {error} — price this one by hand rather than guessing.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !mapsLoaded || status.state === 'no_address'}
          onClick={() => void calculate()}
        >
          {busy ? 'Calculating…' : line ? 'Recalculate' : 'Calculate transport'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setAddressOpen(true)}>
          {overridden ? 'Change pickup address' : 'Pick up elsewhere?'}
        </Button>
      </div>

      {priced && (
        <p className="text-xs text-muted-foreground">
          Last priced {priced.calculated_at ? new Date(priced.calculated_at).toLocaleDateString('en-CA') : ''}
          {priced.distance_method === 'routed' ? ', routed' : ', estimated'}
          {priced.pickup_overridden ? ', from an overridden pickup' : ''}
        </p>
      )}

      <Dialog open={addressOpen} onOpenChange={setAddressOpen}>
        <DialogContent className="cleo-ui sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Pickup address</DialogTitle>
            <DialogDescription>
              Only set this when the body is collected somewhere other than the place of
              death. Transport kilometres are measured from whatever is here.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {mapsLoaded ? (
              <GooglePlacesAutocomplete
                selectProps={{
                  value: picked,
                  onChange: setPicked,
                  placeholder: 'Search for the pickup address…',
                  isClearable: true,
                  styles: { menu: (p: any) => ({ ...p, zIndex: 9999 }) },
                }}
                autocompletionRequest={{ componentRestrictions: { country: ['ca'] } }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Loading address search…</p>
            )}
            {overridden && (
              <p className="mt-2 text-xs text-muted-foreground">
                Currently: {formatAddress((funeral as any)?.pickup_address)}
              </p>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {overridden && (
              <Button variant="ghost" size="sm" onClick={clearPickup}>
                Reset to place of death
              </Button>
            )}
            <Button size="sm" disabled={!picked} onClick={savePickup}>
              Save pickup address
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </Box>
  );
};

export default ContractTransportSummary;
